import loglevel from 'loglevel';
const log = loglevel.getLogger('table-client');
import { BehaviorSubject, Subscription, from, connectable, ReplaySubject, EMPTY, Subject, timer, combineLatest, } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, finalize, map, tap, withLatestFrom, scan, switchMap, share, mergeMap, combineLatestWith, startWith, shareReplay, } from 'rxjs/operators';
import { compareKey } from './keys.js';
import { ConnectionStatus, RowField, RowIdField, } from './models.js';
import { UpdateParser } from './snapshotParser.js';
import { withLatestFromConflated } from './util/withLatestFromConflated.js';
import randomString from './util/randomString.js';
import { convertDescriptorsToMaskMap } from './util/convertDescriptorsToMaskMap.js';
import { QuoteParser } from './quoteParser.js';
import { tapPipe } from './util/tapPipe.js';
export class TableClient {
    client;
    id = randomString();
    _disposed = false;
    get disposed() {
        return this._disposed;
    }
    throttle = 250;
    descriptor$;
    totalsDescriptor$;
    update$;
    updates$;
    totalsUpdate$;
    rowCount$;
    definition$;
    dataRequestSubject = new Subject();
    dataRequest$ = this.dataRequestSubject.asObservable();
    key$;
    get key() {
        return this.key$.value;
    }
    set key(key) {
        if (this.disposed)
            throw new Error('Attempting to set key of disposed table client.');
        if (compareKey(key, this.key))
            return;
        if (key === undefined)
            log.info('tc intentionally setting key to undefined', this.id);
        this.key$.next(key);
    }
    accessCookie$ = new BehaviorSubject(undefined);
    get accessCookie() {
        return this.accessCookie$.value;
    }
    set accessCookie(accessCookie) {
        if (this.disposed)
            throw new Error('Attempting to set accessCookie of disposed table client.');
        this.accessCookie$.next(accessCookie);
    }
    parser = new UpdateParser();
    quoteParser = new QuoteParser();
    shutdownSubscription = new Subscription();
    subscription$;
    _subscription;
    get subscription() {
        return this._subscription;
    }
    rowsByRowId = new Map();
    rowsByRowIndex = new Map();
    getMoreData() {
        if (!this.key)
            return;
        return this._getMoreData(this.subscription, this.key);
    }
    _getMoreData(sub, key) {
        if (key?.ex === 'X' || key?.ex === 'v') {
            this.client.getSnapshot(sub);
        }
        else {
            this.client.getDiff(sub);
        }
        this.dataRequestSubject.next();
    }
    constructor(client, key) {
        this.client = client;
        if (typeof window !== 'undefined')
            window['table'].tables.push(this);
        this.key$ = new BehaviorSubject(key);
        this.key$.subscribe(() => {
            this.rowsByRowId = new Map();
            this.rowsByRowIndex = new Map();
        }); // reset cache when key changes
        this.shutdownSubscription.add(combineLatest({ accessCookie: this.accessCookie$, connectionStatus: client.connectionStatus$ })
            .pipe(filter(({ accessCookie, connectionStatus }) => accessCookie !== undefined && connectionStatus === ConnectionStatus.connected))
            .subscribe(({ accessCookie }) => {
            this.client.addAccessCookie(accessCookie);
        }));
        const subscription$ = connectable(client.connectionStatus$.pipe(filter((x) => x === ConnectionStatus.connected), switchMap(() => this.key$), tap((x) => log.debug('tc', this.id, ': key', x)), withLatestFrom(this.bounds$), //, client.keys$),
        combineLatestWith(client.keys$), map(([[key, bounds], keys]) => {
            if (!key) {
                return [undefined, bounds];
            }
            const found = keys.some((k) => compareKey(k, key));
            log.debug('tc', this.id, ': key', key, found ? 'found in' : 'not found in', keys);
            return [found ? key : undefined, bounds];
        }), // make the key be undefined if the key DNE
        distinctUntilChanged(([key1], [key2]) => {
            // DP: this could be a lambda, but, its broken up with a var assignment for better debugability
            const comparison = compareKey(key1, key2);
            if (!comparison)
                log.debug('tc', this.id, ': key changed from', key1, 'to', key2);
            return comparison;
        }), // so that every new key doesn't cause a resubscribe
        scan((sub, [key, bounds]) => {
            if (sub !== undefined) {
                // old subscription needs cleanup
                log.debug('tc', this.id, ': unsubing', sub);
                client.unsubscribe(sub);
            }
            if (!key) {
                // nothing new to subscribe
                return undefined;
            }
            sub = this.client.subscribe(key, bounds ?? { firstRow: 0, windowSize: Math.random() });
            if (bounds && bounds.windowSize >= 1) {
                // prime the data
                this._getMoreData(sub, key);
            }
            log.debug('tc', this.id, ': subing', sub);
            return sub;
        }, undefined), tap((sub) => (this._subscription = sub)), startWith(undefined)), { connector: () => new ReplaySubject(1) });
        this.subscription$ = subscription$;
        this.shutdownSubscription.add(subscription$.connect());
        const descriptors$ = connectable(client.descriptor$.pipe(withLatestFromConflated(this.subscription$), filter(([x, y]) => x.subscription === y), map(([x]) => x.descriptors)), { connector: () => new ReplaySubject(1) });
        this.shutdownSubscription.add(this.subscription$.subscribe((x) => log.debug('tc', this.id, 'with key', this.key, 'got new subscription', x)));
        this.shutdownSubscription.add(descriptors$.connect());
        this.descriptor$ = descriptors$; /*defer(() => {
            client.getDescriptors(this.subscription);
            return descriptors$;
        });*/
        const definition$ = (this.definition$ = connectable(client.definition$.pipe(withLatestFromConflated(this.subscription$), filter(([x, y]) => x.subscription === y), map(([x]) => x.objects)), { connector: () => new ReplaySubject(1) }));
        this.shutdownSubscription.add(definition$.connect());
        const totalDescriptors$ = connectable(client.totalsDescriptor$.pipe(withLatestFromConflated(this.subscription$), filter(([x, y]) => x.subscription === y), map(([x]) => x.descriptors)), { connector: () => new ReplaySubject(1) });
        this.shutdownSubscription.add(totalDescriptors$.connect());
        /*this.totalsDescriptor$ = defer(() => {
            client.getTotalDescriptors(this.subscription);
            return totalDescriptors$;
        });*/
        this.totalsDescriptor$ = totalDescriptors$;
        // updates
        {
            const snapshotUpdates$ = client.updates$.pipe(withLatestFromConflated(this.subscription$), filter(([x, sub]) => x.subscription === sub), tapPipe(switchMap((x) => timer(this.throttle).pipe(map(() => x))), tap(([_x, sub]) => this.client.getSnapshot(sub))), map(([x]) => x.updates), finalize(() => log.debug('tc', this.id, 'with subscription', this.subscription, 'and key', this.key, 'terminated snapshot subscription')), share());
            const diffs$ = client.diffs$.pipe(withLatestFromConflated(this.subscription$), filter(([x, sub]) => x.subscription === sub), tapPipe(switchMap((x) => timer(this.throttle).pipe(map(() => x))), tap(([_x, sub]) => this.client.getDiff(sub))), map(([x]) => x), finalize(() => log.debug('tc', this.id, 'with subscription', this.subscription, 'and key', this.key, 'terminated diff subscription')), share());
            const maskedDescriptors$ = this.descriptor$.pipe(map((descs) => ({
                fields: descs,
                maskMap: convertDescriptorsToMaskMap(descs),
            })));
            const snapshotsAndDescs$ = snapshotUpdates$.pipe(withLatestFromConflated(maskedDescriptors$), filter(([, descs]) => !!descs));
            const diffsAndDescs$ = diffs$.pipe(withLatestFromConflated(maskedDescriptors$), filter(([, descs]) => !!descs));
            const conflatedRowData = {};
            const parsedQuoteUpdates$ = snapshotsAndDescs$.pipe(map(([updates, { fields }]) => updates.pipe(map((update) => {
                const rowData = this.quoteParser.parse(update.payload, fields);
                rowData[RowField] = update.row;
                rowData[RowIdField] = update.rowId;
                return rowData;
            }))));
            const parsedSnapshotUpdates$ = snapshotsAndDescs$.pipe(
            // filter(x => !!x[0]),
            map(([updates, { fields }]) => updates.pipe(
            // filter(x => x.row >= this.bounds.firstRow && x.row <= this.bounds.firstRow + this.bounds.windowSize),
            map((update) => {
                const buffer = this.rowsByRowId
                    ? this.rowsByRowId.get(update.rowId) ?? {}
                    : conflatedRowData;
                const rowData = this.parser.parse(update.payload, fields, buffer);
                rowData[RowField] = update.row;
                rowData[RowIdField] = update.rowId;
                return rowData;
            }))));
            // let previousRowIndexToRowId: ReadonlyMap<number, number> | undefined = undefined;
            const diffBasedUpdates$ = diffsAndDescs$.pipe(tap(([{ deletedRowIds }]) => {
                for (const rowId of deletedRowIds) {
                    const rowData = this.rowsByRowId.get(rowId);
                    const rowIndex = rowData?.[RowField];
                    if (rowIndex !== undefined)
                        this.rowsByRowIndex.delete(rowIndex);
                    this.rowsByRowId.delete(rowId);
                }
            }), 
            // filter(x => !!x[0]),
            map(([{ deletedRowIds: _deletedRowIds, rowIdToRowIndex, updates }, { fields }]) => {
                const updatedRows = new Map();
                // const rowIndexToRowId: Map<number, number> = new Map();
                // for (const [rowId, rowIndex] of rowIdToRowIndex) {
                //     rowIndexToRowId.set(rowIndex, rowId);
                // }
                for (const [rowId, rowIndex] of rowIdToRowIndex) {
                    const rowData = this.rowsByRowId.get(rowId);
                    // const _clobberedRowData = this.rowsByRowIndex.get(rowIndex);
                    const prevIndex = rowData?.[RowField];
                    if (prevIndex === undefined || prevIndex === rowIndex) {
                        // the row is new: so an update will be found and will handle inclusion in update output
                        // or the row has not moved: so nothing to do here
                        continue;
                    }
                    if (rowData) {
                        updatedRows.set(rowId, rowData);
                        rowData[RowField] = rowIndex;
                        this.rowsByRowIndex.set(rowIndex, rowData);
                        this.rowsByRowIndex.delete(prevIndex);
                    }
                    // The clobbered row should be handled automagically because it should appear in the rowId to rowIndex set as well
                    // If it does not, it must've moved out of bounds
                    // leaving the below code around jic we need it
                    // // include the clobbered row in the update set too
                    // if (clobberedRowData) {
                    //     const clobberedRowNewIndex = rowIdToRowIndex.get(clobberedRowData.[RowIdField]);
                    //     if (clobberedRowNewIndex === undefined) {
                    //         // the row must have moved out of bounds or it would be found
                    //         this.rowsByRowIndex.delete()
                    //     } else {
                    //         clobberedRowData.[RowField] = clobberedRowNewIndex;
                    //         updatedRows.set(clobberedRowData.[RowIdField], clobberedRowData);
                    //     }
                    // }
                }
                // the following subscription should run immediately
                updates.subscribe((update) => {
                    const buffer = this.rowsByRowId.get(update.rowId) ?? {};
                    const rowData = this.parser.parse(update.payload, fields, buffer);
                    rowData[RowField] = update.row;
                    rowData[RowIdField] = update.rowId;
                    this.rowsByRowId.set(update.rowId, rowData);
                    this.rowsByRowIndex.set(update.row, rowData);
                    updatedRows.set(update.rowId, rowData);
                });
                // previousRowIndexToRowId = rowIndexToRowId;
                return from(updatedRows.values());
            }));
            this.updates$ = this.key$.pipe(switchMap((key) => {
                if (!key)
                    return EMPTY;
                if (key.ex === 'X')
                    return parsedSnapshotUpdates$;
                if (key.ex === 'v')
                    return parsedQuoteUpdates$;
                return diffBasedUpdates$;
                // return parsedSnapshotUpdates$;
            }), share());
            this.update$ = this.updates$.pipe(mergeMap((x) => x));
        }
        // totals updates
        {
            const update$ = client.totalsUpdates$.pipe(withLatestFromConflated(this.subscription$), filter(([x, sub]) => x.subscription === sub), tap(([, sub]) => setTimeout(() => this.client.getTotals(sub), this.throttle)), map(([x]) => x.payload));
            const maskedDescriptors$ = this.totalsDescriptor$.pipe(map((descs) => ({
                fields: descs,
                maskMap: convertDescriptorsToMaskMap(descs),
            })));
            const conflatedTotalRowData = {};
            this.totalsUpdate$ = update$.pipe(withLatestFromConflated(maskedDescriptors$), filter(([_update, descs]) => !!descs), map(([update, { fields }]) => this.parser.parse(update, fields, conflatedTotalRowData)), shareReplay({ refCount: true, bufferSize: 1 }));
        }
        const rowCount$ = (this.rowCount$ = connectable(client.rowCount$.pipe(withLatestFromConflated(this.subscription$), 
        // tap(x => logger.log('row count event', x)),
        filter(([x, y]) => x.subscription === y), filter(([, sub]) => sub !== undefined), map(([x]) => x.rowCount)), { connector: () => new ReplaySubject(1) }));
        this.shutdownSubscription.add(rowCount$.connect());
        this.bounds$
            .pipe(debounceTime(100), 
        // tap(x => console.log('incoming bounds change', x)),
        distinctUntilChanged((a, b) => a === b || (a?.firstRow === b?.firstRow && a?.windowSize === b?.windowSize)), filter((x) => !!x), withLatestFromConflated(this.key$, this.subscription$), filter(([, key, sub]) => key !== undefined && sub !== undefined))
            .subscribe(([bounds, key, sub]) => {
            this.client.setBounds(sub, bounds);
            if (key)
                this._getMoreData(sub, key);
            // for (const k of this.rowCache.keys()) {
            //     if (k < bounds.firstRow || k > bounds.firstRow + bounds.windowSize) {
            //         this.rowCache.delete(k);
            //     }
            // }
        });
    }
    // private static convertDescriptorsToMaskMap(fieldDescriptors: FieldDescriptor[]): {[mask: number]: FieldDescriptor} {
    //     return fieldDescriptors.reduce((acc, cur, i) => {
    //         acc[1 << i] = cur;
    //         return acc;
    //     }, {} as any);
    // }
    bounds$ = new BehaviorSubject(undefined);
    get bounds() {
        return this.bounds$.value;
    }
    set bounds(bounds) {
        if (this.disposed)
            throw new Error('Attempting to set bounds of disposed table client.');
        this.bounds$.next(bounds);
    }
    [Symbol.dispose]() {
        if (this.subscription)
            this.client.unsubscribe(this.subscription);
        this.shutdownSubscription.unsubscribe();
        if (typeof window !== 'undefined')
            window['table'].tables = window['table'].tables.filter((x) => x !== this);
        this._disposed = true;
    }
}
export function tableClientForKey(client) {
    const tc = new TableClient(client);
    return (obs) => {
        return obs.pipe(tap((key) => (tc.key = key)), map(() => tc), finalize(() => tc[Symbol.dispose]()));
    };
}
//# sourceMappingURL=tableClient.js.map